const productRoot = () => window.Shopify?.routes?.root || '/';

const cartSectionIds = () => [
  document.querySelector('header')?.closest('[id^="shopify-section-"]')?.id,
  document.querySelector('cart-drawer')?.closest('[id^="shopify-section-"]')?.id
]
  .filter(Boolean)
  .map((id) => id.replace(/^shopify-section-/, ''));

if (!customElements.get('product-details')) {
  customElements.define(
    'product-details',
    class ProductDetails extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialised === 'true') return;
        this.dataset.initialised = 'true';
        this.form = this.querySelector('form[action*="/cart/add"]');
        this.status = this.querySelector('[data-product-status]');
        this.submitButton = this.querySelector('[name="add"]');
        this.galleryDialog = this.querySelector('[data-gallery-dialog]');
        this.galleryOpeners = this.querySelectorAll('[data-gallery-open]');
        this.galleryClose = this.querySelector('[data-gallery-close]');
        this.galleryTrack = this.querySelector('[data-gallery-track]');
        this.galleryPosition = this.querySelector('[data-gallery-position]');
        this.mainGalleryTrack = this.querySelector('[data-main-gallery-track]');
        this.mainGalleryPosition = this.querySelector('[data-main-gallery-position]');
        this.submitting = false;
        this.variantAbortController = null;

        this.addEventListener('change', this.handleOptionChange);
        this.form?.addEventListener('submit', this.handleSubmit);
        this.querySelectorAll('[data-gift-recipient-toggle]').forEach((toggle) => {
          const sync = () => {
            const fieldset = toggle.closest('.gift-recipient');
            fieldset?.classList.toggle('is-enabled', toggle.checked);
            fieldset?.querySelectorAll('.gift-recipient__fields input, .gift-recipient__fields textarea').forEach((field) => {
              field.disabled = !toggle.checked;
              if (field.hasAttribute('data-gift-recipient-required')) field.required = toggle.checked;
            });
          };
          toggle.addEventListener('change', sync);
          sync();
        });
        this.querySelectorAll('[data-character-field]').forEach((field) => {
          field.addEventListener('input', () => this.updateCharacterCount(field));
          this.updateCharacterCount(field);
        });
        this.querySelectorAll('input[type="file"][data-max-file-size-mb]').forEach((field) => {
          field.addEventListener('change', () => this.validateFile(field));
        });
        this.querySelectorAll('[data-postcode-checker]').forEach((checker) => {
          checker.querySelector('[data-postcode-submit]')?.addEventListener('click', () => this.handlePostcodeCheck(checker));
          checker.querySelector('input[autocomplete="postal-code"]')?.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            this.handlePostcodeCheck(checker);
          });
        });
        this.galleryOpeners.forEach((button) => button.addEventListener('click', () => this.openGallery(button)));
        this.galleryClose?.addEventListener('click', () => this.closeGallery());
        this.galleryDialog?.addEventListener('click', (event) => {
          if (event.target === this.galleryDialog) this.closeGallery();
        });
        this.galleryDialog?.addEventListener('close', () => this.galleryPreviouslyFocused?.focus());
        this.galleryTrack?.addEventListener('scroll', this.updateGalleryPosition, { passive: true });
        this.mainGalleryTrack?.addEventListener('scroll', this.updateMainGalleryPosition, { passive: true });
        this.updateGalleryPosition();
        this.updateMainGalleryPosition();
      }

      disconnectedCallback() {
        this.variantAbortController?.abort();
      }

      handleOptionChange = async (event) => {
        const option = event.target.closest('[data-option-value-id]');
        if (!option) return;
        const preservedFormState = this.captureFormState();
        this.dispatchEvent(new CustomEvent('ads:variant_selected', {
          bubbles: true,
          detail: {
            option_name: option.name?.replace(/^options\[|\]$/g, '') || '',
            option_value: option.value || ''
          }
        }));
        const selectedIds = [...this.querySelectorAll('[data-option-value-id]:checked')]
          .map((input) => input.dataset.optionValueId)
          .filter(Boolean);
        const productUrl = option.dataset.productUrl || this.dataset.productUrl;
        const currentProductUrl = new URL(this.dataset.productUrl, window.location.origin);
        const requestUrl = new URL(productUrl, window.location.origin);
        requestUrl.searchParams.set('option_values', selectedIds.join(','));

        this.variantAbortController?.abort();

        if (requestUrl.pathname !== currentProductUrl.pathname) {
          window.location.assign(requestUrl.toString());
          return;
        }

        const controller = new AbortController();
        this.variantAbortController = controller;
        requestUrl.searchParams.set('section_id', this.dataset.sectionId);
        this.setAttribute('aria-busy', 'true');
        try {
          const response = await fetch(requestUrl.toString(), {
            signal: controller.signal,
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          });
          if (!response.ok) throw new Error(`Product section request failed: ${response.status}`);
          const text = await response.text();
          if (controller.signal.aborted) return;
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const replacement = doc.querySelector('product-details');
          if (!replacement) throw new Error('Product section response was incomplete.');
          const historyUrl = new URL(productUrl, window.location.origin);
          historyUrl.searchParams.set('option_values', selectedIds.join(','));
          window.history.replaceState({}, '', historyUrl.toString());
          this.replaceWith(replacement);
          this.restoreFormState(replacement, preservedFormState);
          replacement.querySelector(`[data-option-value-id="${CSS.escape(option.dataset.optionValueId)}"]`)?.focus();
        } catch (error) {
          if (error.name === 'AbortError') return;
          requestUrl.searchParams.delete('section_id');
          window.location.assign(requestUrl.toString());
        } finally {
          if (this.variantAbortController === controller) {
            this.variantAbortController = null;
            this.removeAttribute('aria-busy');
          }
        }
      };

      captureFormState() {
        if (!this.form) return [];
        return [...this.form.elements].flatMap((field) => {
          if (
            !field.name ||
            field.type === 'file' ||
            field.name === 'id' ||
            field.name === 'add' ||
            field.name.startsWith('options[')
          ) return [];

          if (field.tagName === 'SELECT' && field.multiple) {
            return [{
              name: field.name,
              type: 'select-multiple',
              values: [...field.selectedOptions].map((option) => option.value)
            }];
          }
          if (field.type === 'checkbox' || field.type === 'radio') {
            return [{ name: field.name, type: field.type, value: field.value, checked: field.checked }];
          }
          return [{ name: field.name, type: field.type || field.tagName.toLowerCase(), value: field.value }];
        });
      }

      restoreFormState(replacement, state) {
        const form = replacement.querySelector('form[action*="/cart/add"]');
        if (!form || !state.length) return;
        const restoredControls = new Set();

        state.forEach((item) => {
          const controls = [...form.elements].filter((field) => field.name === item.name);
          if (!controls.length) return;

          if (item.type === 'checkbox' || item.type === 'radio') {
            const control = controls.find((field) => field.value === item.value);
            if (!control) return;
            control.checked = item.checked;
            restoredControls.add(control);
            return;
          }

          const control = controls[0];
          if (item.type === 'select-multiple') {
            [...control.options].forEach((option) => {
              option.selected = item.values.includes(option.value);
            });
          } else {
            control.value = item.value;
          }
          restoredControls.add(control);
        });

        restoredControls.forEach((control) => {
          control.dispatchEvent(new Event('input', { bubbles: true }));
          control.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      handleSubmit = async (event) => {
        if (this.submitting || !this.form) return;
        if (!this.form.checkValidity()) {
          event.preventDefault();
          this.form.reportValidity();
          this.form.querySelector(':invalid')?.focus();
          return;
        }
        const fileField = this.form.querySelector('input[type="file"]');
        if (fileField && !this.validateFile(fileField)) {
          event.preventDefault();
          this.form.reportValidity();
          fileField.focus();
          return;
        }
        const selectedFile = fileField?.files?.[0];
        if (selectedFile) {
          this.setStatus('Uploading your reference and adding the configured cake…');
          return;
        }
        if (!window.fetch || !window.FormData) return;
        event.preventDefault();
        this.submitting = true;
        this.setAttribute('aria-busy', 'true');
        const originalLabel = this.submitButton?.textContent;
        if (this.submitButton) {
          this.submitButton.disabled = true;
          this.submitButton.textContent = this.submitButton.dataset.loadingText || 'Adding…';
        }
        this.setStatus('Adding your configured cake to the basket…');

        try {
          const formData = new FormData(this.form);
          const sectionIds = cartSectionIds();
          if (sectionIds.length) formData.append('sections', sectionIds.join(','));
          formData.append('sections_url', window.location.pathname);
          const response = await fetch(`${productRoot()}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: formData
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description || data.message || 'The cake could not be added.');
          window.AngesCart?.renderSections(data.sections || {});
          this.setStatus('Added to your basket.');
          this.dispatchEvent(new CustomEvent('ads:add_to_cart', {
            bubbles: true,
            detail: {
              item_id: String(data.product_id || this.dataset.productId || ''),
              variant_id: String(data.variant_id || ''),
              item_name: data.product_title || this.dataset.productTitle || '',
              quantity: Number(data.quantity || 1),
              price: Number(data.final_price || 0) / 100,
              currency: this.dataset.currency || ''
            }
          }));
          window.AngesCart?.open();
        } catch (error) {
          this.setStatus(error.message || 'The cake could not be added. Please try again.');
          this.querySelector('.product-form__errors')?.focus();
          this.dispatchEvent(new CustomEvent('ads:add_to_cart_error', { bubbles: true, detail: { error_type: 'request_failed' } }));
        } finally {
          this.submitting = false;
          this.removeAttribute('aria-busy');
          if (this.submitButton) {
            this.submitButton.disabled = false;
            this.submitButton.textContent = originalLabel;
          }
        }
      };

      validateFile(field) {
        const file = field.files?.[0];
        const maximum = Number(field.dataset.maxFileSizeMb || 0) * 1024 * 1024;
        const valid = !file || !maximum || file.size <= maximum;
        field.setCustomValidity(valid ? '' : `Choose a file no larger than ${field.dataset.maxFileSizeMb} MB.`);
        return valid;
      }

      updateCharacterCount(field) {
        const output = this.querySelector(`[data-character-count="${CSS.escape(field.id)}"]`);
        if (!output) return;
        const maximum = Number(field.maxLength || 0);
        output.textContent = maximum > 0 ? `${field.value.length} of ${maximum} characters` : `${field.value.length} characters`;
      }

      handlePostcodeCheck = (checker) => {
        const input = checker.querySelector('input');
        const output = checker.querySelector('[data-postcode-result]');
        const postcode = (input?.value || '').toUpperCase().replace(/\s+/g, ' ').trim();
        const valid = /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i.test(postcode);
        if (!valid) {
          if (output) output.textContent = 'Enter a complete UK postcode so we can give a useful indication.';
          input?.setAttribute('aria-invalid', 'true');
          this.dispatchEvent(new CustomEvent('ads:postcode_check', { bubbles: true, detail: { result: 'invalid' } }));
          return;
        }
        input?.removeAttribute('aria-invalid');
        const postcodeArea = postcode.match(/^[A-Z]{1,2}/)?.[0] || '';
        const prefixes = (checker.dataset.prefixes || '').split(',').map((value) => value.trim().toUpperCase()).filter(Boolean);
        const likely = prefixes.includes(postcodeArea);
        if (output) {
          output.textContent = likely
            ? 'This postcode is likely within our usual delivery routes. Final route acceptance is confirmed at checkout.'
            : 'This postcode may sit outside our usual routes. Please check the full delivery page or contact us before ordering.';
        }
        this.dispatchEvent(new CustomEvent('ads:postcode_check', {
          bubbles: true,
          detail: { result: likely ? 'likely_covered' : 'contact_required' }
        }));
      };

      openGallery(button) {
        this.galleryPreviouslyFocused = button;
        const mediaId = button.dataset.galleryOpen;
        if (typeof this.galleryDialog?.showModal === 'function') this.galleryDialog.showModal();
        else this.galleryDialog?.setAttribute('open', '');
        window.requestAnimationFrame(() => {
          this.galleryTrack?.querySelector(`[data-gallery-slide="${CSS.escape(mediaId)}"]`)?.scrollIntoView({ inline: 'start', block: 'nearest' });
          this.galleryClose?.focus();
          this.updateGalleryPosition();
        });
      }

      closeGallery() {
        if (typeof this.galleryDialog?.close === 'function') this.galleryDialog.close();
        else this.galleryDialog?.removeAttribute('open');
      }

      updateMainGalleryPosition = () => {
        if (!this.mainGalleryTrack || !this.mainGalleryPosition) return;
        const slides = [...this.mainGalleryTrack.querySelectorAll('[data-main-gallery-slide]')];
        if (!slides.length) return;
        const left = this.mainGalleryTrack.scrollLeft;
        let closest = 0;
        let distance = Infinity;
        slides.forEach((slide, index) => {
          const delta = Math.abs(slide.offsetLeft - left);
          if (delta < distance) { distance = delta; closest = index; }
        });
        this.mainGalleryPosition.textContent = `${closest + 1} / ${slides.length}`;
      };

      updateGalleryPosition = () => {
        if (!this.galleryTrack || !this.galleryPosition) return;
        const slides = [...this.galleryTrack.querySelectorAll('[data-gallery-slide]')];
        if (!slides.length) return;
        const left = this.galleryTrack.scrollLeft;
        let closest = 0;
        let distance = Infinity;
        slides.forEach((slide, index) => {
          const delta = Math.abs(slide.offsetLeft - left);
          if (delta < distance) {
            distance = delta;
            closest = index;
          }
        });
        this.galleryPosition.textContent = `${closest + 1} / ${slides.length}`;
      };

      setStatus(message) {
        if (this.status) this.status.textContent = message;
      }
    }
  );
}
